import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Bundle quantity picker — lets customers pick 1 / 3 / 5 items with tiered discounts.
 * Implements the same public API as quantity-selector-component (getValue, canAddToCart)
 * so ProductFormComponent can use it transparently.
 *
 * Tier elements are addressed by index through data-tier-* attributes rather
 * than fixed refs, so the number of tiers is whatever the admin configured.
 *
 * @typedef {object} BundlePickerRefs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<BundlePickerRefs>}
 */
class BundleQuantityPicker extends Component {
  requiredRefs = ['quantityInput'];

  #abortController = new AbortController();

  /**
   * Fallback free shipping threshold in cents, used only when the Liquid
   * markup carries no data-free-shipping-threshold. The real figure comes
   * from the shop_shipping_terms metaobject so it stays in step with the
   * cart and the free shipping bar.
   */
  static FREE_SHIPPING_THRESHOLD = 3000;

  /**
   * Tiers as configured in the admin, or the historical 1/3/5 split when the
   * markup carries none.
   * @returns {Array<{quantity: number, discount: number}>}
   */
  get tiers() {
    const raw = this.dataset.tiers;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {
        // Fall through to the default below.
      }
    }
    return [
      { quantity: 1, discount: 0 },
      { quantity: 3, discount: 10 },
      { quantity: 5, discount: 20 },
    ];
  }

  /** @returns {string} text shown when the tier reaches free shipping */
  get freeShippingLabel() {
    return this.dataset.freeShippingLabel || 'Ilmainen toimitus';
  }

  /** @returns {string} text shown when the tier still pays for delivery */
  get shippingFeeLabel() {
    return this.dataset.shippingFeeLabel || '+ toimitus 4,90\u00a0€';
  }

  /** @returns {number} free shipping threshold in cents */
  get freeShippingThreshold() {
    const fromMarkup = Number(this.dataset.freeShippingThreshold);
    return Number.isFinite(fromMarkup) && fromMarkup > 0
      ? fromMarkup
      : BundleQuantityPicker.FREE_SHIPPING_THRESHOLD;
  }

  connectedCallback() {
    super.connectedCallback();

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {string} */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}}
   */
  canAddToCart() {
    return {
      canAdd: true,
      maxQuantity: null,
      cartQuantity: 0,
      quantityToAdd: Number(this.refs.quantityInput.value) || 1,
    };
  }

  /** @param {string} value */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /** Stub for compatibility with ProductFormComponent cart-quantity sync */
  setCartQuantity() {}

  /**
   * Handles card selection.
   * @param {MouseEvent} event
   */
  selectCard(event) {
    const card = /** @type {HTMLElement|null} */ (event.target)?.closest('.bundle-picker__card');
    if (!card) return;

    const qty = card.dataset.quantity;
    if (!qty) return;

    // Update visual state
    const cards = this.querySelectorAll('.bundle-picker__card');
    for (const c of cards) {
      c.classList.remove('bundle-picker__card--selected');
      c.setAttribute('aria-checked', 'false');
    }
    card.classList.add('bundle-picker__card--selected');
    card.setAttribute('aria-checked', 'true');

    // Update hidden input
    this.refs.quantityInput.value = qty;
  }

  /**
   * Recalculates all tier prices when variant changes.
   * @param {import('./events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;
    if (!variant) return;

    const priceInCents = variant.price;
    if (priceInCents == null) return;

    this.dataset.variantPrice = priceInCents.toString();
    this.#updatePrices(priceInCents);
  };

  /**
   * Updates displayed prices and shipping text for every configured tier.
   * @param {number} priceInCents — variant price in cents/minor units
   */
  #updatePrices(priceInCents) {
    const fmt = this.#formatMoney.bind(this);
    const threshold = this.freeShippingThreshold;

    this.tiers.forEach((tier, index) => {
      const quantity = Number(tier.quantity) || 1;
      const discount = Number(tier.discount) || 0;

      // Discount comes off a single unit and is floored to the cent before
      // multiplying, matching how Shopify applies the automatic discount at
      // checkout. Discounting the line total lands a few cents low.
      const unitDiscount = Math.floor((priceInCents * discount) / 100);
      const tierTotal = (priceInCents - unitDiscount) * quantity;
      const tierOriginal = priceInCents * quantity;

      const card = /** @type {HTMLElement|null} */ (this.querySelector(`[data-tier-index="${index}"]`));
      const tokens = {
        quantity,
        discount,
        savings: fmt(unitDiscount * quantity),
        total: fmt(tierTotal),
        unit: fmt(priceInCents - unitDiscount),
      };
      const titleEl = this.querySelector(`[data-tier-title-out="${index}"]`);
      if (titleEl && card?.dataset.tierTitle) titleEl.textContent = this.#renderTokens(card.dataset.tierTitle, tokens);
      const subEl = this.querySelector(`[data-tier-sub-out="${index}"]`);
      if (subEl && card?.dataset.tierSub) subEl.textContent = this.#renderTokens(card.dataset.tierSub, tokens);

      const priceEl = this.querySelector(`[data-tier-price="${index}"]`);
      if (priceEl) priceEl.textContent = fmt(tierTotal);

      const originalEl = this.querySelector(`[data-tier-original="${index}"]`);
      if (originalEl) originalEl.textContent = fmt(tierOriginal);

      const shippingEl = /** @type {HTMLElement|null} */ (
        this.querySelector(`[data-tier-shipping="${index}"]`)
      );
      this.#updateShipping(shippingEl, tierTotal, threshold);
    });
  }

  /**
   * Fills the card text tokens. Mirrors the replace chain in
   * snippets/bundle-quantity-picker.liquid, which renders the first paint.
   * @param {string} template
   * @param {{quantity: number, discount: number, savings: string, total: string, unit: string}} t
   * @returns {string}
   */
  #renderTokens(template, t) {
    return template
      .split('[m\u00e4\u00e4r\u00e4]').join(String(t.quantity))
      .split('[ale]').join(`${t.discount}%`)
      .split('[s\u00e4\u00e4st\u00f6]').join(t.savings)
      .split('[hinta]').join(t.total)
      .split('[kpl-hinta]').join(t.unit);
  }

  /**
   * Updates a shipping element's text and class based on discounted total.
   * @param {HTMLElement|null|undefined} el
   * @param {number} totalCents — discounted total for this tier
   * @param {number} threshold — free shipping threshold in cents
   */
  #updateShipping(el, totalCents, threshold) {
    if (!el) return;
    if (totalCents >= threshold) {
      el.textContent = this.freeShippingLabel;
      el.classList.add('bundle-picker__shipping--free');
    } else {
      el.textContent = this.shippingFeeLabel;
      el.classList.remove('bundle-picker__shipping--free');
    }
  }

  /**
   * Formats a price in minor units using the shop's money format.
   * Falls back to simple EUR formatting if Shopify's format string isn't available.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');

    // Use Shopify's money_format if available
    if (typeof Theme !== 'undefined' && Theme.moneyFormat) {
      return Theme.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString());
    }

    return `${amount}\u00a0€`;
  }
}

if (!customElements.get('bundle-quantity-picker')) {
  customElements.define('bundle-quantity-picker', BundleQuantityPicker);
}
